import {compact} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import {convertUserTimeZone} from '../../../../../../_gori/helpers'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import UseRoutesPermission from '../../../../../../_gori/hooks/UseRoutesPermission'
import {useAuth} from '../../../../auth'
import {Industry, MonthlyShipmentVolume} from '../../../core/_const'
import SettingsService from '../../../core/_requests'
import {AccountEditModal} from './AccountEditModal'
import {ChangePasswordModal} from './ChangePasswordModal'
import {CompanyEditModal} from './CompanyEditModal'
import clsx from 'clsx'
const ProfilePage: React.FC = () => {
  const intl = useIntl()
  const {routes} = UseRoutesPermission()
  const {loadingSwitch, currentUser} = useAuth()
  const {newCancelToken, isCancel} = useCancelToken()
  const [company, setCompany] = useState<any>()
  const [showModal, setShowModal] = useState<{
    change_password: boolean
    account_edit: boolean
    connections_edit: boolean
  }>({
    change_password: false,
    account_edit: false,
    connections_edit: false,
  })

  const buildSkeleton = useMemo(() => {
    return (
      <>
        <div className='mt-6'>
          <div className='d-flex justify-content-center'>
            <div className='w-100 w-sm-75 bg-light rounded p-8'>
              <div className='d-flex justify-content-between'>
                <div className='fs-1 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <span className='fs-1 col-1 placeholder rounded-2 bg-secondary' />
              </div>
              <div className='fs-2 col-12 placeholder placeholder-lg rounded-2 bg-secondary mt-6 mb-10' />
              <div className='col-12 d-flex justify-content-between'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
              <div className='col-12 d-flex justify-content-between mt-6'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
            </div>
          </div>
        </div>

        <div className='mt-6'>
          <div className='d-flex justify-content-center'>
            <div className='w-100 w-sm-75 bg-light rounded p-8'>
              <div className='d-flex justify-content-between'>
                <div className='fs-1 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <span className='fs-1 col-1 placeholder rounded-2 bg-secondary' />
              </div>
              <div className='fs-2 col-12 placeholder placeholder-lg rounded-2 bg-secondary mt-6 mb-10' />
              <div className='col-12 d-flex justify-content-between'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
              <div className='col-12 d-flex justify-content-between mt-6'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }, [])

  const getProfileConnection = useCallback(async () => {
    try {
      const company = await SettingsService.getCompany({
        cancelToken: newCancelToken(),
      })

      if (company) {
        setCompany(company)
      }
    } catch (error: any) {
      if (isCancel(error)) return
    }
  }, [isCancel, newCancelToken])

  useEffect(() => {
    if (loadingSwitch) return
    getProfileConnection()

    return () => {}
  }, [getProfileConnection, loadingSwitch])

  const PROFILE_AND_CONNECTIONS = useMemo(() => {
    if (!company)
      return {
        ACCOUNT: [],
        COMPANY: [],
        PRIMARY_CONTACTS: [],
      }
    const currentAccount = currentUser?.current_account
    const {accounting, report} = company
    const {street1, city, state, zip} = currentAccount ?? {}
    const valuesAddress = compact([street1, city, state])
    const ShowAddress = `${valuesAddress.join(', ')} ${zip ?? ''}`

    return {
      ACCOUNT: [
        {
          label: 'FIRST_NAME',
          value: currentUser?.first_name ?? null,
        },
        {
          label: 'LAST_NAME',
          value: currentUser?.last_name ?? null,
        },
        {
          label: 'EMAIL',
          value: currentUser?.email ?? null,
        },
      ],
      COMPANY: [
        {
          label: 'ORGANIZATION_NAME',
          value: company?.name ?? null,
        },
        {
          label: 'COMPANY_NAME',
          value: currentUser?.current_account?.name ?? null,
        },
        {
          label: 'PHONE',
          value: currentAccount?.phone ?? null,
        },
        {
          label: 'MONTHLY_SHIPMENT_VOLUME',
          value:
            intl.formatMessage({
              id:
                MonthlyShipmentVolume.find(
                  (option) => company?.monthly_shipment_volume === option.value
                )?.label || intl.formatMessage({id: 'NONE'}),
            }) ?? null,
        },
        {
          label: 'COUNTRY',
          value: currentAccount?.country ?? null,
        },
        {
          label: 'BUSINESS_ADDRESS',
          value: ShowAddress ?? null,
        },
        {
          label: 'COMPANY_WEBSITE',
          value: company?.website_url ?? null,
        },
        {
          label: 'INDUSTRY',
          value:
            intl.formatMessage({
              id:
                Industry.find((option) => company?.industry === option.value)?.label ||
                intl.formatMessage({id: 'NONE'}),
            }) ?? null,
        },
        {
          label: 'SHIPPER_CODE',
          value: currentUser?.current_account?.shipper_code ?? null,
        },
        {
          label: 'SALE_REP',
          value: company?.sales_rep ?? null,
        },
      ],
      PRIMARY_CONTACTS: [
        {
          label: 'ALL_REPORTS',
          value: report?.name ?? null,
        },
        {
          label: null,
          value: report?.emails?.join(`\n`) ?? null,
        },
        {
          label: 'ACCOUNTING',
          value: accounting?.name ?? null,
        },
        {
          label: null,
          value: accounting?.emails?.join(`\n`) ?? null,
        },
      ],
    }
  }, [company, currentUser, intl])

  return (
    <>
      {showModal.connections_edit && (
        <CompanyEditModal
          show={showModal.connections_edit}
          handleClose={() => {
            setShowModal((prev) => ({...prev, connections_edit: false}))
          }}
          company={company}
        />
      )}
      {showModal.account_edit && (
        <AccountEditModal
          show={showModal.account_edit}
          handleClose={() => {
            setShowModal((prev) => ({...prev, account_edit: false}))
          }}
        />
      )}
      {showModal.change_password && (
        <ChangePasswordModal
          show={showModal.change_password}
          handleClose={() => {
            setShowModal((prev) => ({...prev, change_password: false}))
          }}
        />
      )}

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='mb-4'>
          {!company ? (
            <>{buildSkeleton}</>
          ) : (
            <div className='d-flex flex-column align-items-center justify-content-center'>
              <div className='d-flex justify-content-start w-100 settings-header'>
                <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
                  {intl.formatMessage({id: 'PROFILE'})}
                </h1>
              </div>
              <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
                <div className='d-flex flex-wrap justify-content-between'>
                  <div>
                    <span className='fs-3 fw-bolder text-gray-700'>
                      {intl.formatMessage({id: 'USER_INFORMATION'})}
                    </span>
                    <p className='form-label text-gray-600'>
                      {intl.formatMessage({id: 'USER_CREATED'}) + ': '}
                      {convertUserTimeZone(
                        currentUser?.created_at ?? '',
                        currentUser,
                        process.env.REACT_APP_DATE_FORMAT
                      )}
                    </p>
                  </div>
                  <div className='fw-bold d-flex' style={{height: 'fit-content'}}>
                    <div
                      className='fs-5 btn btn-advanced-tertiary px-2 py-1 cursor-pointer fw-bolder'
                      onClick={() => setShowModal((prev) => ({...prev, change_password: true}))}
                    >
                      {intl.formatMessage({id: 'CHANGE_PASSWORD'})}
                    </div>
                    <div
                      className='fs-5 btn btn-advanced-tertiary px-2 py-1 cursor-pointer fw-bolder ms-4'
                      onClick={() => setShowModal((prev) => ({...prev, account_edit: true}))}
                    >
                      {intl.formatMessage({id: 'EDIT'})}
                    </div>
                  </div>
                </div>

                <div className='mt-6'>
                  {PROFILE_AND_CONNECTIONS.ACCOUNT.map((row, index) => (
                    <div className='d-flex text-gray-700 fs-5 mt-8' key={index}>
                      <div className='col-6 col-sm-4 fw-bold'>
                        {row.label ? intl.formatMessage({id: row.label}) : null}
                      </div>
                      <div className='text-truncate fw-bolder'>
                        {row?.value || intl.formatMessage({id: 'NONE'})}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {routes.ADMINS.hasPermission && (
                <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
                  <div>
                    <div className='d-flex justify-content-between'>
                      <span className='fs-3 fw-bolder text-gray-700'>
                        {intl.formatMessage({id: 'BUSINESS_PROFILE'})}
                      </span>
                      <div
                        className='fs-5 btn btn-advanced-tertiary px-2 py-1 cursor-pointer fw-bolder'
                        onClick={() => setShowModal((prev) => ({...prev, connections_edit: true}))}
                      >
                        {intl.formatMessage({id: 'EDIT'})}
                      </div>
                    </div>
                    <div className='mt-6'>
                      {PROFILE_AND_CONNECTIONS?.COMPANY?.map((row, index) => (
                        <div className='d-flex text-gray-700 fs-5 mt-8' key={index}>
                          <div className='col-6 col-sm-4 fw-bold'>
                            {row.label ? intl.formatMessage({id: row.label}) : null}
                          </div>
                          <div className='text-wrap fw-bolder'>
                            {row?.value || intl.formatMessage({id: 'NONE'})}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <hr className='my-8 fw-bold' />
                  <div>
                    <div className='d-flex'>
                      <span className='fs-3 fw-bolder text-gray-700'>
                        {intl.formatMessage({id: 'PRIMARY_CONTACTS'})}
                      </span>
                    </div>
                    <div className='mt-6'>
                      {PROFILE_AND_CONNECTIONS?.PRIMARY_CONTACTS?.map((row, index) => (
                        <div
                          className={clsx('d-flex text-gray-700 fs-5', row.label ? 'mt-8' : '')}
                          key={index}
                        >
                          <div className='col-6 col-sm-4 fw-bold'>
                            {row.label ? intl.formatMessage({id: row.label}) : null}
                          </div>
                          <div
                            className={clsx('text-truncate fw-bolder', row.label ? '' : 'mt-2')}
                            style={{whiteSpace: 'pre'}}
                          >
                            {row?.value || intl.formatMessage({id: 'NONE'})}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CSSTransition>
    </>
  )
}

export {ProfilePage}
